import { DSTooltip } from '@repo/design-system/index'
import { CAF_ANALYSIS_TYPE_MAP } from '../../../../../model/caf.interface'
import { useCafLessonDiffV5 } from '../../../../../modules/apis/student/lesson'
import { useCafAnalysisComponentDetailV5, useCafScoresV5 } from '../../../../../modules/apis/webview/caf'
import { BOX_BORDER_CLX, CAF_LEVEL_MAX } from '../../../../../modules/consts/caf'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import findWordingKey from '../../../../../modules/hooks/webview/caf/common/findWordingKey'
import useCafLessonDiffData from '../../../../../modules/hooks/webview/caf/common/useCafLessonDiffData'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { ICON_QUESTION_GRAY700 } from '../../../../../modules/svg/svg'
import { COLORS } from '../../../../../modules/vars'
import LoadingSpinner from '../../../../common/LoadingSpinner'
import Card from '../../common/Card'
import HighlightedText from '../../common/HighlightedText'
import { useWebviewModal } from '../../common/Modal/WebviewModal'
import { ProgressBarNoDetail } from '../../common/ProgressBox'
import { LevelDescription } from '../LevelDescription'

const ACCURACY_TAG = 2
const AccuracyIntroSection = ({ componentId }) => {
  const { data, isLoading } = useCafAnalysisComponentDetailV5(+componentId)
  const { data: scoreData } = useCafScoresV5('epc')
  const webviewPopup = useWebviewModal()
  const { data: diffData } = useCafLessonDiffV5()
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const handleClickTooltip = () => {
    webviewPopup.show({
      id: 'epc_popup',
      title: t(n_caf.accuracy.modal_title),
      body: t(n_caf.accuracy.modal_body),
    })
  }
  const epcList = [
    {
      title: isMobile ? (
        <div className="flex items-center gap-[6px]">
          {t(n_caf.accuracy.my_epc)} <img src={ICON_QUESTION_GRAY700} onClick={handleClickTooltip} alt="question" />
        </div>
      ) : (
        <DSTooltip
          place="bottom"
          isMultiline={true}
          isOpenOnClick={true}
          triggerComponent={
            <div className="flex items-center gap-[6px]">
              {t(n_caf.accuracy.my_epc)} <img src={ICON_QUESTION_GRAY700} alt="question" />
            </div>
          }
          contentComponent={t(n_caf.accuracy.modal_body)}
        />
      ),
      value: data?.epc || '-',
      color: COLORS.PURPLE50,
    },
    {
      title: t(n_caf.accuracy.top_25_epc),
      value: scoreData?.top_25_ratio?.value || '-',
      color: COLORS.YELLOW50,
    },
    {
      title: t(n_caf.accuracy.tutor_epc),
      value: scoreData?.native_ratio?.value || '-',
      color: COLORS.BLUE50,
    },
  ]
  const { diffDataAsTag, isFirst, Icon, diffColors, title, description } = useCafLessonDiffData(diffData, ACCURACY_TAG)

  if (isLoading || !data)
    return (
      <div className="flex h-[100px] w-[100%] items-center justify-center">
        <LoadingSpinner show primary />
      </div>
    )

  return (
    <>
      <Card clx="mb-[12px]">
        <div className="flex justify-between">
          <span className="text-[20px] font-bold text-purple-500">
            <span className="text-purple-500 ">
              {t(n_caf.accuracy.level_score)} {data?.level_rough}
            </span>
            <span className="text-gray-200">/9</span>
          </span>
        </div>
        <ProgressBarNoDetail levelExact={data?.level_exact} totalNum={CAF_LEVEL_MAX} hideFraction />
        {isFirst ? (
          <div className="mt-[12px] flex w-full items-center justify-center rounded-[5px] bg-gray-50 px-16 py-4 text-gray-700">
            <div className="text-[14px] text-gray-700">{t(n_caf.trend.score_detail)}</div>
            <div className="ml-16 text-[14px] font-bold text-gray-700">{data?.level_exact || 0}</div>
          </div>
        ) : (
          <div className="flex flex-row items-center justify-start">
            <div className="mt-[12px] flex w-full items-center justify-center rounded-[5px] bg-gray-50 px-16 py-4 text-gray-700">
              <div className="border-r-1 flex w-[50%] flex-row justify-center border-gray-100">
                <div className="text-[14px] text-gray-700">{t(n_caf.past_lesson)}</div>
                <div className="ml-16 text-[14px] font-bold text-gray-700">
                  {(Number(data?.level_exact || 0) - Number(diffDataAsTag?.band_diff || 0)).toFixed(1)}
                </div>
              </div>
              <div className="flex w-[50%] flex-row justify-center">
                <div className="text-[14px] text-gray-700">{t(n_caf.this_lesson)}</div>
                <div className="ml-16 text-[14px] font-bold text-gray-700">{data?.level_exact || 0}</div>
              </div>
            </div>
            <div
              className="mt-12 ml-4 flex flex-row items-center justify-center rounded-[4px] py-4 pl-5 pr-10"
              style={{ backgroundColor: diffColors.bg }}
            >
              <Icon />
              {diffDataAsTag.band_diff !== 0 && (
                <span className="text-[14px] font-bold" style={{ color: diffColors.text }}>
                  {Math.abs(diffDataAsTag.band_diff).toFixed(1)}
                </span>
              )}
            </div>
          </div>
        )}
      </Card>
      {/* percent section */}
      <div className={`${BOX_BORDER_CLX} mb-[12px] flex py-[8px] px-[4px]`}>
        {epcList?.map((epcItem, idx) => (
          <p key={idx + 'epcItem'} className="flex-1 pl-[8px] first-of-type:border-r last-of-type:border-l">
            <div className="text-sBody pb-[8px]">{epcItem.title}</div>
            <HighlightedText color={epcItem.color} text={epcItem.value} />
          </p>
        ))}
      </div>

      {isFirst ? (
        <p className="text-sBody mb-[24px]">
          {t(findWordingKey(n_caf, { key: [CAF_ANALYSIS_TYPE_MAP[2], `caf_level${data?.level_rough}`] }))}
        </p>
      ) : (
        <div
          className="border-1 relative mb-12 rounded-[8px] p-12"
          style={{
            borderColor: diffColors.border,
          }}
        >
          <div
            className="absolute left-0 top-0 rounded-tl-[8px] rounded-br-[8px] px-[12px] py-[4px] text-[14px] font-bold"
            style={{
              color: diffColors.text,
              backgroundColor: diffColors.bg,
            }}
          >
            {title}
          </div>
          <div className="mt-[23px] text-[14px] text-gray-600">
            {description
              ? description
              : t(findWordingKey(n_caf, { key: [CAF_ANALYSIS_TYPE_MAP[2], `caf_level${data?.level_rough}`] }))}
          </div>
        </div>
      )}

      <LevelDescription status={'Accuracy'} level={data?.level_rough} />
      <div className="h-24" />
    </>
  )
}

export default AccuracyIntroSection
